import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Lock, CheckCircle2, Circle, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RecapCard({ s, token, onSaved }) {
  const [text, setText] = useState(s.recap_text || "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { setText(s.recap_text || ""); }, [s.recap_text]);
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/curiosity/recap/${s.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      toast.success("Recap saved");
      onSaved();
    } catch (e) { toast.error("Couldn't save the recap. Please try again."); }
    finally { setSaving(false); }
  };
  const showPreview = async () => {
    try {
      const res = await fetch(`${API}/admin/curiosity/recap/${s.id}/preview`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPreview(data.html);
    } catch (e) { toast.error("Couldn't load the preview."); }
  };
  const due = new Date(s.recap_due_utc);
  const overdue = due.getTime() < Date.now();
  return (
    <div data-testid={`recap-card-${s.id}`} className="bg-[#F7F5F0] border hairline rounded-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-xl tracking-tight">{s.label}</h3>
        {s.recap_sent ? (
          <span data-testid={`recap-sent-${s.id}`} className="inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#5C6B50] bg-[#5C6B50]/10 px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Sent to {s.recap_sent_count} guest{s.recap_sent_count === 1 ? "" : "s"}{s.recap_sent_at ? ` · ${new Date(s.recap_sent_at).toLocaleString()}` : ""}
          </span>
        ) : (
          <span className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
            {overdue ? "Sends within a minute of saving" : `Sends automatically at ${due.toLocaleString()}`}
          </span>
        )}
      </div>
      <textarea data-testid={`recap-text-${s.id}`} rows={4} value={text} onChange={(e) => setText(e.target.value)}
        disabled={s.recap_sent}
        placeholder="What we explored together, key takeaways, anything you promised to share…"
        className="mt-3 w-full bg-white border hairline rounded-sm px-3 py-2 text-sm text-[#4a4642] outline-none focus:border-[#B85C38] resize-none disabled:opacity-60" />
      {!s.recap_sent && (
        <div className="mt-2 flex items-center gap-5">
          <button data-testid={`recap-save-${s.id}`} onClick={save} disabled={saving}
            className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border-b border-[#B85C38] pb-0.5 hover:text-[#B85C38] disabled:opacity-50">
            {saving ? "Saving…" : "Save recap"}
          </button>
          <button data-testid={`recap-preview-${s.id}`} onClick={showPreview}
            className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#9a938a] border-b border-[#9a938a] pb-0.5 hover:text-[#B85C38] hover:border-[#B85C38]">
            Preview email
          </button>
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setPreview(null)} data-testid={`recap-preview-modal-${s.id}`}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b hairline">
              <span className="font-mono-a text-[11px] uppercase tracking-[0.15em] text-[#595550]">Exactly what your guests will receive</span>
              <button data-testid={`recap-preview-close-${s.id}`} onClick={() => setPreview(null)} className="font-mono-a text-[11px] uppercase tracking-[0.15em] text-[#9a938a] hover:text-[#B85C38]">Close ✕</button>
            </div>
            <iframe title="Email preview" srcDoc={preview} className="flex-1 w-full min-h-[60vh]" sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
}

const REMINDERS = [
  ["24h", "24h"],
  ["1h", "1h"],
  ["5min", "5m"],
];

export default function AdminCuriosity() {
  const [token, setToken] = useState(localStorage.getItem("aw_admin_token") || "");
  const [input, setInput] = useState("");
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subs, setSubs] = useState([]);
  const [wk, setWk] = useState({ registrations: [], session: null, sessions: [] });
  const [audits, setAudits] = useState([]);
  const [priority, setPriority] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/admin/curiosity`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) { setError("That access code isn't right. Please try again."); setBookings([]); localStorage.removeItem("aw_admin_token"); setToken(""); return; }
      const data = await res.json();
      setBookings(data.bookings || []);
      setSessions(data.sessions || []);
      try {
        const nres = await fetch(`${API}/admin/newsletter`, { headers: { Authorization: `Bearer ${t}` } });
        if (nres.ok) { const ndata = await nres.json(); setSubs(ndata.subscribers || []); }
        const wres = await fetch(`${API}/admin/workshop`, { headers: { Authorization: `Bearer ${t}` } });
        if (wres.ok) { const wdata = await wres.json(); setWk({ registrations: wdata.registrations || [], session: wdata.session, sessions: wdata.sessions || [] }); }
        const ares = await fetch(`${API}/admin/audit`, { headers: { Authorization: `Bearer ${t}` } });
        if (ares.ok) { const adata = await ares.json(); setAudits(adata.audits || []); }
        const pres = await fetch(`${API}/admin/priority`, { headers: { Authorization: `Bearer ${t}` } });
        if (pres.ok) { const pdata = await pres.json(); setPriority(pdata.members || []); }
      } catch (e) { /* non-critical */ }
    } catch (e) {
      setError("Couldn't load bookings. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const unlock = (e) => {
    e.preventDefault();
    localStorage.setItem("aw_admin_token", input.trim());
    setToken(input.trim());
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name || b.email}'s sign-up? This can't be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/curiosity/${b.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(`Deleted ${b.name || b.email}`);
      setBookings((prev) => prev.filter((x) => x.id !== b.id));
    } catch (e) { toast.error("Couldn't delete. Please try again."); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#EAE5D9] grid place-items-center px-6">
        <form onSubmit={unlock} className="w-full max-w-sm text-center" data-testid="curiosity-admin-login">
          <div className="w-14 h-14 rounded-full bg-[#2C2A28] grid place-items-center mx-auto"><Lock className="w-6 h-6 text-[#F7F5F0]" /></div>
          <h1 className="font-serif text-4xl tracking-tighter mt-6">Curiosity Chats</h1>
          <p className="mt-2 text-[#595550] text-sm">Enter your private access code.</p>
          <input data-testid="curiosity-admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Access code" className="mt-6 w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
          {error && <p className="mt-3 text-sm text-[#B85C38]">{error}</p>}
          <button data-testid="curiosity-admin-unlock-button" type="submit" className="reserve-cta mt-4 w-full bg-[#2C2A28] text-[#F7F5F0] py-3 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#B85C38]">Unlock</button>
          <Link to="/" className="block mt-6 font-mono-a text-[10px] uppercase tracking-[0.18em] text-[#595550] hover:text-[#B85C38]">← Back to site</Link>
        </form>
      </div>
    );
  }

  const copyWkEmails = async () => {
    const emails = wk.registrations.map((r) => r.email);
    if (!emails.length) { toast.info("No registrations yet."); return; }
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (e) { window.prompt("Copy the emails below:", emails.join(", ")); }
  };

  const downloadWkCsv = () => {
    if (!wk.registrations.length) { toast.info("No registrations yet."); return; }
    const rows = [["first_name", "email", "session", "registered_at"], ...wk.registrations.map((r) => [r.first_name || "", r.email, r.session_label || "", r.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "five-energy-leaks-registrations.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const removeWk = async (r) => {
    if (!window.confirm(`Remove ${r.first_name || r.email} from the workshop list?`)) return;
    try {
      const res = await fetch(`${API}/admin/workshop/${r.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setWk((prev) => ({ ...prev, registrations: prev.registrations.filter((x) => x.id !== r.id) }));
      toast.success("Registration removed");
    } catch (e) { toast.error("Couldn't remove — please try again."); }
  };

  const copyEmails = async () => {
    const emails = subs.map((s) => s.email);
    if (!emails.length) { toast.info("No subscribers yet."); return; }
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (e) { window.prompt("Copy the emails below:", emails.join(", ")); }
  };

  const downloadCsv = () => {
    if (!subs.length) { toast.info("No subscribers yet."); return; }
    const rows = [["email", "name", "source", "in_crm", "subscribed_at"], ...subs.map((s) => [s.email, s.name || "", s.source || "footer", s.fg_pushed ? "yes" : "no", s.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "vita-nuova-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const AUDIT_KEYS = ["pleasure", "rest", "beauty", "connection", "aliveness"];
  const copyAuditEmails = async () => {
    const emails = audits.map((a) => a.email);
    if (!emails.length) { toast.info("No audit submissions yet."); return; }
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (e) { window.prompt("Copy the emails below:", emails.join(", ")); }
  };

  const downloadAuditCsv = () => {
    if (!audits.length) { toast.info("No audit submissions yet."); return; }
    const header = ["first_name", "email", "total", "tier", "fullest", "thinnest", ...AUDIT_KEYS.map((k) => `${k}_avg`), "submitted_at"];
    const rows = [header, ...audits.map((a) => [
      a.first_name || "", a.email, a.total, a.tier_name || a.tier || "", a.fullest || "", a.thinnest || "",
      ...AUDIT_KEYS.map((k) => (a.averages || {})[k] ?? ""), a.created_at || "",
    ])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = "dolce-vita-audit.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  const removeAudit = async (a) => {
    if (!window.confirm(`Remove ${a.first_name || a.email}'s audit?`)) return;
    try {
      const res = await fetch(`${API}/admin/audit/${a.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setAudits((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Audit removed");
    } catch (e) { toast.error("Couldn't remove — please try again."); }
  };

  const copyPriorityEmails = async () => {
    const emails = priority.map((p) => p.email).filter(Boolean);
    if (!emails.length) { toast.info("No priority sign-ups yet."); return; }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(emails.join(", "));
      } else {
        const ta = document.createElement("textarea");
        ta.value = emails.join(", ");
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (e) { window.prompt("Copy the emails below:", emails.join(", ")); }
  };

  const downloadPriorityCsv = () => {
    if (!priority.length) { toast.info("No priority sign-ups yet."); return; }
    const rows = [["first_name", "email", "status", "joined_at"], ...priority.map((p) => [p.first_name || "", p.email, p.status || "", p.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = "priority-list.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  const removePriority = async (p) => {
    if (!window.confirm(`Remove ${p.first_name || p.email} from the priority list?`)) return;
    try {
      const res = await fetch(`${API}/admin/priority/${p.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setPriority((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Removed from priority list");
    } catch (e) { toast.error("Couldn't remove — please try again."); }
  };

  const copyPhones = async (list) => {
    const phones = list.map((b) => b.phone).filter(Boolean);
    if (!phones.length) { toast.info("No phone numbers in this session yet."); return; }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(phones.join(", "));
      } else {
        const ta = document.createElement("textarea");
        ta.value = phones.join(", ");
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${phones.length} phone number${phones.length === 1 ? "" : "s"}`);
    } catch (e) {
      window.prompt("Copy the phone numbers below:", phones.join(", "));
    }
  };

  // group by session label
  const groups = bookings.reduce((acc, b) => {
    const key = b.session_label || b.session_id || "—";
    (acc[key] = acc[key] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#EAE5D9]">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link to="/" className="font-serif text-xl tracking-tight">The Ageless Woman</Link>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-2">Curiosity Chats <span className="text-[#B85C38]">({bookings.length})</span></h1>
            <Link to="/admin/applications" className="mt-2 inline-block font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] hover:text-[#B85C38]">→ Applications inbox</Link>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="curiosity-admin-refresh" onClick={() => load(token)} className="inline-flex items-center gap-2 border hairline rounded-full px-4 py-2 font-mono-a text-[11px] uppercase tracking-[0.15em] hover:border-[#B85C38]">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => { localStorage.removeItem("aw_admin_token"); setToken(""); }} className="font-mono-a text-[11px] uppercase tracking-[0.15em] text-[#595550] hover:text-[#B85C38]">Lock</button>
          </div>
        </div>

        <div className="mt-4 bg-[#F7F5F0] border hairline rounded-sm px-6 py-4 font-mono-a text-[11px] uppercase tracking-[0.12em] text-[#595550]">
          Each sign-up gets an instant Zoom confirmation, plus reminders at 24h · 1h · 5min. Dots below show which have been sent.
        </div>

        {sessions.length > 0 && (
          <div className="mt-10" data-testid="recap-section">
            <h2 className="font-serif text-3xl tracking-tighter">Post-chat recaps</h2>
            <p className="mt-2 text-[#595550] text-sm max-w-2xl">Write a short recap after each session — what you explored, key takeaways. It goes out automatically <strong>3 hours after the session</strong> (or as soon as you save it, if later) to everyone who registered, together with the 48-hour <strong>$100-off application</strong> invitation. Anyone who hasn't applied also gets a gentle "closes soon" nudge 6 hours before their deadline.</p>
            <div className="mt-4 space-y-4">
              {sessions.map((s) => <RecapCard key={s.id} s={s} token={token} onSaved={() => load(token)} />)}
            </div>
          </div>
        )}

        {loading && bookings.length === 0 && <p className="mt-10 text-[#595550]">Loading…</p>}
        {!loading && bookings.length === 0 && <p data-testid="curiosity-admin-empty" className="mt-10 text-[#595550]">No sign-ups yet. They'll appear here the moment someone books.</p>}

        <div className="mt-8 space-y-8" data-testid="curiosity-admin-list">
          {Object.entries(groups).map(([label, list]) => (
            <div key={label}>
              <h2 className="font-serif text-2xl tracking-tight border-b hairline pb-2 flex items-center justify-between gap-3 flex-wrap">
                <span>{label} <span className="text-[#B85C38] text-lg">({list.length})</span></span>
                <button data-testid="copy-phones-button" onClick={() => copyPhones(list)}
                  className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                  Copy phone numbers
                </button>
              </h2>
              <div className="mt-4 space-y-3">
                {list.map((b) => (
                  <div key={b.id} data-testid="curiosity-booking-card" className="bg-[#F7F5F0] border hairline rounded-sm p-5 flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="font-serif text-xl tracking-tight">{b.name || "—"}</h3>
                      <a href={`mailto:${b.email}`} className="text-[#B85C38] hover:underline text-sm">{b.email}</a>
                      {b.phone && <span className="text-[#595550] text-sm"> · {b.phone}</span>}
                      <div className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#9a938a]">
                        {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full ${b.confirmation_sent ? "text-[#5C6B50] bg-[#5C6B50]/10" : "text-[#B85C38] bg-[#B85C38]/10"}`}>
                        {b.confirmation_sent ? <><CheckCircle2 className="w-3 h-3" /> Confirmation sent</> : <><Circle className="w-3 h-3" /> No confirmation</>}
                      </div>
                      <div className="mt-2 flex items-center justify-end gap-3">
                        {REMINDERS.map(([key, lbl]) => {
                          const sent = (b.reminders_sent || {})[key];
                          return (
                            <span key={key} className="inline-flex items-center gap-1 font-mono-a text-[10px] uppercase tracking-[0.1em]" title={`${lbl} reminder`}>
                              {sent ? <CheckCircle2 className="w-3.5 h-3.5 text-[#5C6B50]" /> : <Circle className="w-3.5 h-3.5 text-[#c9c1b3]" />}
                              <span className={sent ? "text-[#5C6B50]" : "text-[#9a938a]"}>{lbl}</span>
                            </span>
                          );
                        })}
                      </div>
                      {(b.nudge_sent || b.nudge_skipped) && (
                        <div data-testid="nudge-status" className="mt-1.5 font-mono-a text-[10px] uppercase tracking-[0.1em]">
                          {b.nudge_skipped === "applied"
                            ? <span className="text-[#5C6B50]">✓ Applied — no nudge needed</span>
                            : <span className="text-[#B8893C]">$100 nudge sent</span>}
                        </div>
                      )}
                      <button data-testid="curiosity-delete-button" onClick={() => remove(b)}
                        title="Delete this sign-up"
                        className="mt-2 inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12" data-testid="workshop-section">
          <div className="flex items-center justify-between flex-wrap gap-3 border-b hairline pb-2">
            <div>
              <h2 className="font-serif text-3xl tracking-tighter">Workshop — The Five Energy Leaks <span className="text-[#B85C38] text-xl">({wk.registrations.length})</span></h2>
              {wk.sessions?.length ? (
                <div className="mt-1 space-y-0.5">
                  {wk.sessions.map((s) => (
                    <p key={s.id} className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                      {s.label} · {s.sub} <span className="text-[#B85C38]">({wk.registrations.filter((r) => (r.session_id || wk.sessions[0].id) === s.id).length})</span>
                    </p>
                  ))}
                </div>
              ) : (wk.session && <p className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">{wk.session.label} · {wk.session.sub}</p>)}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="workshop-copy-emails" onClick={copyWkEmails}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Copy all emails
              </button>
              <button data-testid="workshop-download-csv" onClick={downloadWkCsv}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Download CSV
              </button>
            </div>
          </div>
          {wk.registrations.length === 0 ? (
            <p data-testid="workshop-empty" className="mt-4 text-[#595550] text-sm">No registrations yet — they'll appear here as soon as someone saves a seat at /workshop.</p>
          ) : (
            <div className="mt-4 bg-[#F7F5F0] border hairline rounded-sm divide-y divide-[#E4DFD3]" data-testid="workshop-list">
              {wk.registrations.map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-[#2C2A28]">{r.first_name}</span>
                    <a href={`mailto:${r.email}`} className="text-[#B85C38] hover:underline text-sm">{r.email}</a>
                    {r.session_label && <span className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#5C6B50] border border-[#5C6B50]/30 rounded-full px-2 py-0.5">{r.session_label}</span>}
                    <span className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#9a938a]">
                      {r.confirmation_sent ? "✓ confirmed" : "confirmation pending"}
                      {r.reminders_sent?.["24h"] ? " · 24h ✓" : ""}{r.reminders_sent?.["1h"] ? " · 1h ✓" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                    </span>
                    <button data-testid="workshop-delete-button" onClick={() => removeWk(r)}
                      className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12" data-testid="audit-section">
          <div className="flex items-center justify-between flex-wrap gap-3 border-b hairline pb-2">
            <h2 className="font-serif text-3xl tracking-tighter">Dolce Vita Audit <span className="text-[#B85C38] text-xl">({audits.length})</span></h2>
            <div className="flex items-center gap-2">
              <button data-testid="audit-copy-emails" onClick={copyAuditEmails}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Copy all emails
              </button>
              <button data-testid="audit-download-csv" onClick={downloadAuditCsv}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Download CSV
              </button>
            </div>
          </div>
          {audits.length === 0 ? (
            <p data-testid="audit-empty" className="mt-4 text-[#595550] text-sm">No audit submissions yet — they'll appear here the moment someone completes the audit at /dolce-vita-audit.</p>
          ) : (
            <div className="mt-4 bg-[#F7F5F0] border hairline rounded-sm divide-y divide-[#E4DFD3]" data-testid="audit-list">
              {audits.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-start justify-between flex-wrap gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm text-[#2C2A28]">{a.first_name}</span>
                      <a href={`mailto:${a.email}`} className="text-[#B85C38] hover:underline text-sm">{a.email}</a>
                      <span className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#C9A24B] bg-[#C9A24B]/12 px-2 py-0.5 rounded-full font-bold">{a.total}/25</span>
                      <span className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#595550] bg-[#E4DFD3] px-2 py-0.5 rounded-full">{a.tier_name}</span>
                    </div>
                    <div className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                      Fullest: {a.fullest} · Thinnest: <span className="text-[#B85C38]">{a.thinnest}</span>
                      {a.averages && <span className="text-[#c9c1b3]"> · {AUDIT_KEYS.map((k) => (a.averages[k] != null ? Number(a.averages[k]).toFixed(1) : "-")).join("/")}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                      {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                    </span>
                    <button data-testid="audit-delete-button" onClick={() => removeAudit(a)}
                      className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12" data-testid="priority-section">
          <div className="flex items-center justify-between flex-wrap gap-3 border-b hairline pb-2">
            <h2 className="font-serif text-3xl tracking-tighter">Priority List <span className="text-[#B85C38] text-xl">({priority.length})</span></h2>
            <div className="flex items-center gap-2">
              <button data-testid="priority-copy-emails" onClick={copyPriorityEmails}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Copy all emails
              </button>
              <button data-testid="priority-download-csv" onClick={downloadPriorityCsv}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Download CSV
              </button>
            </div>
          </div>
          {priority.length === 0 ? (
            <p data-testid="priority-empty" className="mt-4 text-[#595550] text-sm">No priority sign-ups yet — they'll appear here the moment someone joins at /priority.</p>
          ) : (
            <div className="mt-4 bg-[#F7F5F0] border hairline rounded-sm divide-y divide-[#E4DFD3]" data-testid="priority-list">
              {priority.map((p) => (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-[#2C2A28]">{p.first_name}</span>
                    <a href={`mailto:${p.email}`} className="text-[#B85C38] hover:underline text-sm">{p.email}</a>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                      {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                    </span>
                    <button data-testid="priority-delete-button" onClick={() => removePriority(p)}
                      className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a] hover:text-[#B85C38]">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12" data-testid="newsletter-section">
          <div className="flex items-center justify-between flex-wrap gap-3 border-b hairline pb-2">
            <h2 className="font-serif text-3xl tracking-tighter">Vita Nuova subscribers <span className="text-[#B85C38] text-xl">({subs.length})</span></h2>
            <div className="flex items-center gap-2">
              <button data-testid="newsletter-copy-emails" onClick={copyEmails}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Copy all emails
              </button>
              <button data-testid="newsletter-download-csv" onClick={downloadCsv}
                className="font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#595550] border hairline rounded-full px-3 py-1.5 hover:border-[#B85C38] hover:text-[#B85C38]">
                Download CSV
              </button>
            </div>
          </div>
          {subs.length === 0 ? (
            <p data-testid="newsletter-empty" className="mt-4 text-[#595550] text-sm">No subscribers yet — they'll appear here the moment someone joins Vita Nuova from the site footer.</p>
          ) : (
            <div className="mt-4 bg-[#F7F5F0] border hairline rounded-sm divide-y divide-[#E4DFD3]" data-testid="newsletter-list">
              {subs.map((s) => (
                <div key={s.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <a href={`mailto:${s.email}`} className="text-[#B85C38] hover:underline text-sm">{s.email}</a>
                    <span data-testid="subscriber-source" className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#595550] bg-[#E4DFD3] px-2 py-0.5 rounded-full">
                      {s.source || "footer"}
                    </span>
                    {s.fg_pushed && (
                      <span title="Pushed to Funnel Gorgeous" className="font-mono-a text-[9px] uppercase tracking-[0.12em] text-[#5C6B50] bg-[#5C6B50]/10 px-2 py-0.5 rounded-full">
                        CRM ✓
                      </span>
                    )}
                  </div>
                  <span className="font-mono-a text-[10px] uppercase tracking-[0.12em] text-[#9a938a]">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
